import { useEffect, useState } from 'react';

// Se lee como archivo estático servido junto al build (public/maintenance.json),
// no como parte del bundle de JS: para activar/desactivar la mantención basta
// con sobreescribir ese único archivo en el servidor, sin recompilar ni
// redesplegar toda la app.
const MAINTENANCE_URL = '/maintenance.json';
const POLL_INTERVAL = 30 * 1000; // 30s: las sesiones ya abiertas deben notar el cambio pronto
const BYPASS_PARAM = 'maint_key';
const BYPASS_STORAGE_KEY = 'maintenance_bypass';

const hasValidBypass = () => {
  const bypassKey = process.env.REACT_APP_MAINTENANCE_BYPASS_KEY;
  if (!bypassKey) return false;

  const params = new URLSearchParams(window.location.search);
  const provided = params.get(BYPASS_PARAM);
  if (provided && provided === bypassKey) {
    try { sessionStorage.setItem(BYPASS_STORAGE_KEY, '1'); } catch {}
    return true;
  }

  try {
    return sessionStorage.getItem(BYPASS_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const isSameStatus = (a, b) =>
  a.checked === b.checked && a.active === b.active && a.title === b.title && a.message === b.message;

// Layer 4 del sistema de sesión (ver appContext.js): igual que el watcher de
// sesión cada 3 min, pero consultando un flag de mantención en vez del token.
// Se ejecuta por fuera de AppContext porque debe bloquear TODAS las rutas
// (incluido /auth) y correr también para usuarios sin sesión iniciada.
export const useMaintenanceStatus = () => {
  const [status, setStatus] = useState({ checked: false, active: false, title: '', message: '' });

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const response = await fetch(`${MAINTENANCE_URL}?t=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) throw new Error('maintenance.json no disponible');
        const data = await response.json();
        if (cancelled) return;

        const active = !!data.active && !hasValidBypass();
        const next = {
          checked: true,
          active,
          title: data.title || 'Mantención programada',
          message: data.message || 'El sistema no está disponible en este momento.',
        };
        // Devolver el mismo objeto si nada cambió: un setState con objeto nuevo
        // cada 30s re-renderiza App completo (y todo el árbol de rutas) aunque
        // maintenance.json siga igual.
        setStatus(prev => (isSameStatus(prev, next) ? prev : next));
      } catch {
        // Si el archivo no carga (red caída, 404, etc.) no bloqueamos el acceso.
        if (!cancelled) {
          setStatus(prev => (prev.checked && !prev.active ? prev : { ...prev, checked: true, active: false }));
        }
      }
    };

    check();
    const intervalId = setInterval(check, POLL_INTERVAL);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  return status;
};
